import { useSearchBrowseData } from '@aibees/elephant/context/search-browse-data-provider';
import { PeopleTableInfo } from '@aibees/elephant/feature/search-table-people';
import { useBrowsePeopleSearchCore } from '@aibees/elephant/feature/search-table-people/hooks/use-browse-people-search';
import {
  useSearchModeFromRoute,
  useSearchQueryFilter,
  useSearchRouteInfo,
} from '@aibees/elephant/hooks';
import { useModalityChangeCounterStore } from '@aibees/elephant/store/modality-change-counter';
import { SearchTable } from '@aibees/elephant/types';
import { useAdvanceTableConfigContext } from '@aibees/shared/features/advance-table/context';
import { useBoolean } from '@aibees/shared/hooks';
import {
  decodeQueryParamValues,
  encodeQueryParamValues,
} from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useDeferredValue, useMemo, useRef } from 'react';
import { useBrowseCompanySearchCore } from '../../../hooks/use-browse-company-search';
import { CompanyTableInfo } from '../../../types';
import { useSelectedLookalikeKey } from './use-selected-lookalike-key';

/**
 * Lookalike handler that uses shared `top` from context. Does not call
 * useBrowseCompanySearchCore or useBrowsePeopleSearchCore. Use this in table
 * rows so each row does not invoke the browse API hooks.
 */
export function useLookalikeHandlerWithTop(
  currentItem: CompanyTableInfo | PeopleTableInfo,
  top: number
) {
  const { params, setParam } = useSearchQueryFilter();
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const open = useBoolean(false);
  const { lookalikeKey } = useSelectedLookalikeKey(currentItem);
  const currentDomains = useMemo(
    () => decodeQueryParamValues(params.value),
    [params.value]
  );
  const { maxStickyRows } = useAdvanceTableConfigContext();

  const changeOpen = useCallback(
    (value: boolean) => {
      if (value) {
        open.onTrue();
        if (timeoutId.current) clearTimeout(timeoutId.current);
        timeoutId.current = setTimeout(() => {
          open.onFalse();
        }, 5000);
      } else {
        open.onFalse();
      }
    },
    [open]
  );

  const handleClick = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();

      if (!lookalikeKey) return;

      const isSelected = currentDomains.includes(lookalikeKey);

      if (!isSelected && maxStickyRows && top >= maxStickyRows) {
        changeOpen(true);
        return;
      }

      const updatedDomains = isSelected
        ? currentDomains.filter((key) => key !== lookalikeKey)
        : [...currentDomains, lookalikeKey];

      const encodedDomains = encodeQueryParamValues(updatedDomains);

      setParam('value', encodedDomains, true);
    },
    [lookalikeKey, currentDomains, maxStickyRows, top, setParam, changeOpen]
  );

  return useMemo(
    () => ({ handleClick, changeOpen, open }),
    [handleClick, changeOpen, open]
  );
}

export function useLookalikeHandler(
  currentItem: CompanyTableInfo | PeopleTableInfo
) {
  const browseData = useSearchBrowseData();
  const { type, table } = useSearchRouteInfo();
  const mode = useSearchModeFromRoute();
  const workspace = useWorkspace()?.id;
  const { params, setParam } = useSearchQueryFilter();
  const deferredParams = useDeferredValue(params);
  const { counter: modalityVersion } = useModalityChangeCounterStore();

  const companyDeps = {
    workspace,
    type,
    mode,
    searchParams: deferredParams,
    modalityVersion,
  };
  const peopleDeps = {
    workspace,
    type,
    searchParams: deferredParams,
    modalityVersion,
  };

  const useContextData = browseData != null;
  const companyResult = useBrowseCompanySearchCore(
    {},
    companyDeps,
    {
      enabled: !useContextData && table === SearchTable.Company,
    },
    true
  );
  const peopleResult = useBrowsePeopleSearchCore(
    {},
    peopleDeps,
    {
      enabled: !useContextData && table === SearchTable.People,
    },
    true
  );

  const companyData = useContextData
    ? browseData?.companyData
    : companyResult.data;
  const peopleData = useContextData
    ? browseData?.peopleData
    : peopleResult.data;

  const top =
    (table === SearchTable.People
      ? peopleData?.top?.length
      : companyData?.top?.length) || 0;
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const open = useBoolean(false);

  const { lookalikeKey } = useSelectedLookalikeKey(currentItem);
  const currentDomains = useMemo(
    () => decodeQueryParamValues(params.value),
    [params.value]
  );
  const { maxStickyRows } = useAdvanceTableConfigContext();

  const changeOpen = useCallback(
    (value: boolean) => {
      if (value) {
        open.onTrue();
        if (timeoutId.current) clearTimeout(timeoutId.current);
        timeoutId.current = setTimeout(() => {
          open.onFalse();
        }, 5000);
      } else {
        open.onFalse();
      }
    },
    [open]
  );

  const handleClick = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      event?.stopPropagation();

      if (!lookalikeKey) return;

      const isSelected = currentDomains.includes(lookalikeKey);

      // Prevent adding more keys if maximum sticky rows are reached
      if (!isSelected && maxStickyRows && top >= maxStickyRows) {
        changeOpen(true);
        return;
      }

      // Update domains based on selection state
      const updatedDomains = isSelected
        ? currentDomains.filter((key) => key !== lookalikeKey)
        : [...currentDomains, lookalikeKey];

      const encodedDomains = encodeQueryParamValues(updatedDomains);

      setParam('value', encodedDomains, true);
    },
    [lookalikeKey, currentDomains, maxStickyRows, top, setParam, changeOpen]
  );

  return useMemo(
    () => ({ handleClick, changeOpen, open }),
    [handleClick, changeOpen, open]
  );
}
